import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus, User, X } from 'lucide-react';
import { Heading } from 'components/Heading';
import { MobileScreen } from 'components/MobileScreen';
import { Para } from 'components/Para';
import { Input } from 'components/Input';
import { Button } from 'components/Button';
import { useStore } from 'engine/store';
import { Spacer } from 'components/Spacer';
import { emitMessage } from 'engine/api';
import { toast } from 'sonner';

export const Route = createFileRoute('/register-members')({
	component: RouteComponent,
});

function RouteComponent() {
	const eventId = useStore((s) => s.eventId);
	const teamId = useStore((s) => s.teamId);
	const setMembers = useStore((s) => s.setMembers);
	const setRatId = useStore((s) => s.setRatId);

	const [names, setNames] = useState<string[]>(['']);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [confirmedNames, setConfirmedNames] = useState<string[]>([]);

	const navigate = useNavigate();
	const setPlayState = useStore((s) => s.setPlayState);

	const addNameField = () => {
		setNames([...names, '']);
	};

	const removeNameField = (index: number) => {
		setNames(names.filter((_, i) => i !== index));
	};

	const updateName = (index: number, value: string) => {
		const newNames = [...names];
		newNames[index] = value;

		setNames(newNames);
	};

	const checkDuplicateNames = (names: string[]): boolean => {
		return names.some((name, i) => names.some((n, j) => n.trim().toLowerCase() === name.trim().toLowerCase() && i !== j));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (checkDuplicateNames(names)) return toast(__`Each team member must have a unique name`);
		const filteredNames = names.filter((name) => name.trim() !== '');
		if (filteredNames.length > 2) {
			setConfirmedNames(filteredNames);
			setShowConfirmation(true);
		} else {
			toast(__`Add at least three players to continue`);
		}
	};

	const handleChange = () => {
		setShowConfirmation(false);
	};

	const handleAccept = async () => {
		if (!eventId || !teamId) return;

		const result = await emitMessage({
			kind: 'event-register-members',
			id: eventId,
			teamId: teamId,
			members: confirmedNames,
		});

		if (!result.success) {
			toast.error(__`Failed to register team members. Try again later.`);
			return;
		}

		setMembers(result.members!);
		setRatId(result.ratId!);

		setPlayState('showing-rat');
		navigate({
			to: '/showing-rat',
			viewTransition: { types: ['slide-left'] },
		});
	};

	return (
		<>
			{!showConfirmation && (
				<MobileScreen className="flex flex-col">
					<Heading mode="primary">{__`Register team members`}</Heading>
					<Para>{__`Please register your team members:`}</Para>

					<form onSubmit={handleSubmit} className="mt-6 flex grow flex-col space-y-4">
						{names.map((name, index) => (
							<div key={index} className="flex items-center gap-2">
								<div className="grow">
									<Input
										autoFocus={true}
										placeholder={__`Name`}
										value={name}
										onChange={(e) => updateName(index, e.target.value)}
										className="w-full"
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												if (names.length < 3) {
													e.preventDefault();
													addNameField();
												}
											}
										}}
									/>
								</div>
								{names.length > 1 && (
									<button
										type="button"
										onClick={() => removeNameField(index)}
										className="rounded-full bg-white p-2 text-black"
									>
										<X className="size-5" />
									</button>
								)}
							</div>
						))}

						<Button type="button" onClick={addNameField} className="w-full !bg-white !text-black">
							<Plus className="size-4" />
							{__`Add team member`}
						</Button>

						<Button type="submit" className="w-full">
							{__`Submit`}
						</Button>
					</form>
				</MobileScreen>
			)}

			{showConfirmation && (
				<MobileScreen className="flex flex-col">
					<Heading mode="primary">{__`Confirm team members`}</Heading>
					<div className="mt-4"></div>
					<Para>{__`These will be your team members:`}</Para>

					<ul className="mt-6 list-inside list-disc space-y-4">
						{confirmedNames.map((name, index) => (
							<div key={index} className="flex gap-2">
								<User />
								{name}
							</div>
						))}
					</ul>
					<Spacer />
					<div className="mt-auto flex gap-4 pt-6">
						<Button onClick={handleChange} className="flex-1 !bg-white !text-black">
							{__`Change`}
						</Button>
						<Button autoFocus={true} onClick={handleAccept} className="flex-1">
							{__`Accept`}
						</Button>
					</div>
				</MobileScreen>
			)}
		</>
	);
}
